/* StadiArt.com progressive enhancements. Intentionally isolated from Dawn's commerce UI. */
(function () {
  'use strict';

  var stateKey = '__stadiartComMotion';
  if (window[stateKey]) return;

  var state = (window[stateKey] = {
    observer: null,
    observed: new WeakSet(),
    parallax: new WeakSet(),
    initialized: false,
  });

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)');

  function isComExperience() {
    return Boolean(document.body && document.body.classList.contains('sa-com'));
  }

  function motionIsReduced() {
    return Boolean(reducedMotion && reducedMotion.matches);
  }

  function reveal(element) {
    if (!element) return;
    element.classList.add('is-revealed');
    element.removeAttribute('data-sa-reveal-pending');
  }

  function revealAll(root) {
    getElements(root, '[data-sa-reveal]').forEach(reveal);
  }

  function getElements(root, selector) {
    var scope = root && root.querySelectorAll ? root : document;
    var elements = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (root && root.nodeType === 1 && root.matches && root.matches(selector)) elements.unshift(root);
    return elements;
  }

  function getRevealObserver() {
    if (state.observer || !('IntersectionObserver' in window)) return state.observer;

    state.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          state.observer.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -10%', threshold: 0.08 }
    );

    return state.observer;
  }

  function initialiseReveals(root) {
    var reveals = getElements(root, '[data-sa-reveal]');
    if (motionIsReduced() || !('IntersectionObserver' in window)) {
      reveals.forEach(reveal);
      return;
    }

    document.body.classList.add('sa-motion-ready');
    var observer = getRevealObserver();
    reveals.forEach(function (element) {
      if (state.observed.has(element) || element.classList.contains('is-revealed')) return;
      state.observed.add(element);
      element.setAttribute('data-sa-reveal-pending', '');
      observer.observe(element);
    });
  }

  function numericStrength(value) {
    var parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 10;
    return Math.min(Math.max(parsed, 0), 18);
  }

  function initialiseParallax(root) {
    if (motionIsReduced() || !finePointer || !finePointer.matches) return;

    getElements(root, '[data-sa-parallax]').forEach(function (element) {
      if (state.parallax.has(element)) return;
      state.parallax.add(element);

      var frame = null;
      var pointer = null;
      var strength = numericStrength(element.getAttribute('data-sa-parallax'));

      function reset() {
        if (frame) window.cancelAnimationFrame(frame);
        frame = null;
        pointer = null;
        element.style.setProperty('--sa-parallax-x', '0px');
        element.style.setProperty('--sa-parallax-y', '0px');
      }

      function render() {
        frame = null;
        if (!pointer) return;
        var bounds = element.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        var x = (pointer.clientX - bounds.left) / bounds.width - 0.5;
        var y = (pointer.clientY - bounds.top) / bounds.height - 0.5;
        element.style.setProperty('--sa-parallax-x', (x * strength * 2).toFixed(2) + 'px');
        element.style.setProperty('--sa-parallax-y', (y * strength * 2).toFixed(2) + 'px');
      }

      element.addEventListener(
        'pointermove',
        function (event) {
          pointer = event;
          if (!frame) frame = window.requestAnimationFrame(render);
        },
        { passive: true }
      );
      element.addEventListener('pointerleave', reset, { passive: true });
      element.addEventListener('pointercancel', reset, { passive: true });
    });
  }

  function updateHeaderState() {
    if (!isComExperience()) return;
    var scrolled = window.scrollY > 24;
    document.body.classList.toggle('sa-has-scrolled', scrolled);
    getElements(document, '[data-sa-header]').forEach(function (header) {
      header.classList.toggle('is-scrolled', scrolled);
    });
  }

  function focusAnchorTarget(target) {
    if (!target) return;
    var hadTabIndex = target.hasAttribute('tabindex');
    if (!hadTabIndex) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (!hadTabIndex) {
      window.setTimeout(function () {
        target.removeAttribute('tabindex');
      }, 0);
    }
  }

  function onAnchorClick(event) {
    if (!isComExperience() || motionIsReduced()) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest && event.target.closest('a[data-sa-anchor]');
    if (!link || link.target || link.hasAttribute('download')) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '#' || href === '#') return;

    var target;
    try {
      target = document.querySelector(href);
    } catch (error) {
      return;
    }
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.requestAnimationFrame(function () {
      focusAnchorTarget(target);
    });
    if (window.history && window.history.pushState) window.history.pushState(null, '', href);
  }

  function initialise(root) {
    if (!isComExperience()) return;
    initialiseReveals(root || document);
    initialiseParallax(root || document);
    updateHeaderState();
  }

  function start() {
    if (state.initialized) return;
    state.initialized = true;
    initialise(document);

    window.addEventListener('scroll', updateHeaderState, { passive: true });
    document.addEventListener('click', onAnchorClick);
    document.addEventListener('shopify:section:load', function (event) {
      initialise(event.target);
    });

    if (reducedMotion && reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', function () {
        if (!isComExperience()) return;
        if (motionIsReduced()) {
          if (state.observer) state.observer.disconnect();
          document.body.classList.remove('sa-motion-ready');
          revealAll(document);
        } else {
          initialise(document);
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
